import { Dialog } from '@capacitor/dialog';
import { useResetPassword } from 'app/core/api/auth/api-auth';
import { appEnv } from 'app/core/environment';
import Button from 'app/shared/components/button/Button';
import Icon from 'app/shared/components/icon/Icon';
import IconLib from 'app/shared/components/icon/icon-lib';
import { ErrorText } from 'app/shared/components/misc';
import Body from 'app/shared/components/typography/body/Body';
import Heading from 'app/shared/components/typography/heading/Heading';
import Form from 'app/shared/frames/Form';
import { BackButton } from 'app/shared/page-parts/HeaderBar';
import SharedRoutePath from 'app/shared/SharedRoutePath';
import { Opt } from 'app/tools/general.types';
import { LSKey } from 'app/tools/hooks/local-storage/local-storage.types';
import useLocalState from 'app/tools/hooks/local-storage/use-local-storage';
import _ from 'lodash';
import { useSubscription } from 'observable-hooks';
import React, { useState } from 'react';
import { noop, Subject } from 'rxjs';
import { filter, mapTo, scan, tap } from 'rxjs/operators';
import FormTextInput from '../components/form-input/FormTextInput';
import useInputForm from '../components/form-input/utilities/use-input-form';

type FormValues = {
  email: string;
};

type ClickEvent = 1 | 2 | undefined;
let clickTimeout: Opt<NodeJS.Timeout>;
/** Channel for secret clicks on icon (value = 1) and title (value = 2).
 * A value of undefined resets the click events recording */
const devActivationClick$ = new Subject<ClickEvent>();
/** Shortcut function to reset the click events recording */
const resetClicks = () => devActivationClick$.next(undefined);
/** Channel which monitors the click events and emits `true` if the
 * clicks match the correct pattern within the time limit. Otherwise
 * emits nothing */
const devActivationSuccess$ = devActivationClick$.pipe(
  // only enable Dev-Mode activation if in prod environment
  filter(() => appEnv.API_ROOT.includes('backend-prod-dot')),
  // accumulate clicks into an array, and emit that array each time
  scan((acc = [], curr) => {
    // if `undefined` value, let it pass through and set acc to it too.
    // this means on the next click, acc is undefined and so will
    // default to `[]`, effectively resetting the click recording.
    if (!curr) return curr;
    // return accumulated clicks with the current click appended.
    return [...acc, curr];
  }, [] as Opt<ClickEvent[]>),
  tap(clicks => {
    if (!clicks) {
      // if `undefined` (reset flag) arrives, clear any timeout
      if (clickTimeout) clearTimeout(clickTimeout);
    } else if (clicks.length === 1) {
      // otherwise if it is the first click (length === 1), start a
      // timeout that will reset click recording after 6 seconds.
      // this means click pattern has to be executed within 6s.
      clickTimeout = setTimeout(resetClicks, 6000);
    }
  }),
  filter(clicks =>
    // only let events continue if they equal the correct sequence
    _.isEqual(clicks, [1, 1, 1, 2, 2, 2, 1, 1, 2, 2, 1, 2])
  ),
  // make sure clicks are reset, just to be on the safe side
  tap(resetClicks),
  mapTo(true as const)
);

export default function ResetPassword(): JSX.Element {
  const [resetSent, setResetSent] = useState(false);
  const [, setDevMode] = useLocalState(LSKey.DEV_TESTING_MODE, false);

  const { request$, sending, error } = useResetPassword();

  // eslint-disable-next-line @typescript-eslint/unbound-method
  const { handleSubmit, inputProps } = useInputForm<FormValues>({
    mode: 'onTouched',
    apiError: error,
  });

  const submit$ = (data: FormValues) =>
    request$(data).pipe(
      tap({
        next: () => setResetSent(true),
      })
    );

  useSubscription(devActivationSuccess$, () => {
    Dialog.prompt({
      title: 'Password Required',
      message: 'Enter your password to access Dev-Mode',
    })
      .then(pwInput => {
        if (pwInput.value === '114-magic-door') setDevMode(true);
      })
      .catch(noop);
  });

  return !resetSent ? (
    <>
      <BackButton absolutePos />
      <Icon
        icon={IconLib.LOCK}
        soloContentStyle
        // NOTE: click on lock icon to trigger a click event `1`
        onClick={() => devActivationClick$.next(1)}
      />
      <Heading
        color="primary"
        // NOTE: click on page heading to trigger a click event `2`
        onClick={() => devActivationClick$.next(2)}
      >
        Reset password
      </Heading>
      <Body antiMargin>
        Enter the email associated with your account and we'll send
        you an email with instructions to reset your password.
      </Body>
      <Form onSubmit={handleSubmit(submit$)}>
        <FormTextInput
          controlName="email"
          autoComplete="email"
          type="email"
          icon={IconLib.MAIL}
          classicPlaceholder
          {...inputProps}
        />
        <ErrorText message={error} />
        <Button type="submit" pending={sending}>
          Reset password
        </Button>
      </Form>
    </>
  ) : (
    <>
      <BackButton absolutePos />
      <Icon icon={IconLib.SEND} soloContentStyle />
      <Heading color="primary">Check your email</Heading>
      <Body antiMargin>
        Almost there! Check your email inbox for instructions on
        resetting your password.
      </Body>
      <Button variant="outlined" to={SharedRoutePath.LOGIN}>
        Back to login
      </Button>
    </>
  );
}
